import {
  Dialog,
  FormGroup,
  H4,
  HTMLSelect,
  NumericInput,
  Icon,
} from "@blueprintjs/core";
import { Colors } from "design/colors";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThrottleRangeDisplay } from "components/ThrottleRangeDisplay";
import {
  ControlMode,
  IWriteControlConfigRequest,
  IWritePowerConfigResponse,
  InputMode,
  IWritePositionControlSettingsRequest,
  PositionType,
  PositionResolution,
  PositionUnits,
  HomingCalibStatus,
} from "utils/common-types";
import {
  Strings,
  isPolePairCountValid as _isPolePairCountValid,
  P_GAIN_TEXT,
  I_GAIN_TEXT,
  D_GAIN_TEXT,
  MOTOR_MAX_SPEED_TEXT,
  MOTOR_MAX_ACCELERATION_TEXT,
  DEADBAND_TEXT,
  POSITION_TYPE_TEXT,
  POSITION_UNITS_TEXT,
  RESOLUTION_TEXT,
  TRAVEL_DISTANCE_TEXT,
} from "utils/common";
import {
  InputConfig,
  PosCtrlCalResults,
} from "utils/firestore";
import {
  MIN_RPM_STEP,
  MIN_STEP,
} from "utils/throttle";
import "./Config.css";
import { useSingleTelemetryValue } from "hooks/useSingleTelemetryValue";
import { Button } from "components/Button";
import { ToggleButton } from "components/toggleButton/ToggleButton";
import { Tooltip2 } from "@blueprintjs/popover2";
import { centerItemsStyle, configContainerStyle, configModalStyle, inputStyle, positionButtonContStyle, screenTitleStyle } from "./CStoreEditor";
import { readDareZeroResult, runHomingCalibration, zeroDareSensor } from "connections/controller";
import { packetThreadPoolApi } from "utils/packet-workers";
import { useDevices } from "hooks/useDevices";

const FORM_WIDTH = 350;
const POSITION_UPDATE_INTERVAL_MS = 50;

const homingStatusToString = (status: HomingCalibStatus): string => {
  switch (status) {
    case HomingCalibStatus.SUCCESS:
      return "Success";
    case HomingCalibStatus.FAILURE_TIMEOUT:
      return "Failed: Timeout";
    case HomingCalibStatus.FAILURE_NO_STALL:
      return "Failed: No stall detected";
    case HomingCalibStatus.FAILURE_NOT_FREEWHEEL:
      return "Failed: Motor not in freewheel";
    case HomingCalibStatus.IN_PROGRESS:
      return "In Progress...";
    default:
      return "Unknown";
  }
};

interface INavigationProps {
  setEnableNavigation: (enable: boolean) => void;
  next: (() => void) | undefined;
  prev: (() => void) | undefined;
}

type Edit<T> = (key: keyof T, value: string) => void;

export const isPolePairCountValid = (
  polePairCount: string | number
): boolean => {
  const ppc =
    typeof polePairCount === "string"
      ? parseFloat(polePairCount)
      : polePairCount;
  return _isPolePairCountValid(ppc);
};

const throttleErrors = (
  inputConfig: Strings<InputConfig>,
  controlConfig: Strings<IWriteControlConfigRequest>
): string[] => {
  type StepSizeInfo = {
    label: string;
    step: number;
    stepStr: string | undefined;
    units: string;
    minStep: number;
  };
  const stepSizes: StepSizeInfo[] = [
    {
      label: "torque",
      step: parseFloat(inputConfig.ampsStep ?? ""),
      stepStr: inputConfig.ampsStep,
      units: "A",
      minStep: MIN_STEP,
    },
    {
      label: "speed",
      step: parseFloat(inputConfig.rpmStep ?? ""),
      stepStr: inputConfig.rpmStep,
      units: "RPM",
      minStep: MIN_RPM_STEP,
    },
  ];

  const createStepSizeErrorStr = (info: StepSizeInfo) => {
    const prefix = `Throttle step size for ${info.label} control mode`;
    if (info.stepStr === "") {
      return `${prefix} must be supplied.`;
    } else if (!isFinite(info.step)) {
      return `${prefix} is ${info.stepStr} ${info.units} which is invalid.`;
    } else {
      return `${prefix} is ${info.stepStr} ${info.units} which is less than minimum of ${info.minStep} ${info.units}.`;
    }
  };

  const isInputModeUSB = inputConfig.inputMode === InputMode.USB.toString();
  let errors;
  const areStepSizesValidated = isInputModeUSB;
  if (areStepSizesValidated) {
    errors = stepSizes
      .filter(
        (info: StepSizeInfo) => !isFinite(info.step) || info.step < info.minStep
      )
      .map(createStepSizeErrorStr);

    const controlMode: ControlMode = parseInt(controlConfig.controlMode, 10);
    const motorMaxSpeed = parseFloat(controlConfig.motorMaxSpeed); // Call this "Max Throttle Speed" in the UI

    if (controlMode === ControlMode.SPEED) {
      if (!isFinite(motorMaxSpeed)) {
        errors.push("Max Throttle Speed is an invalid value.");
      } else if (inputConfig.rpmStep !== undefined) {
        const step = parseFloat(inputConfig.rpmStep);
        if (isFinite(step) && motorMaxSpeed < step) {
          errors.push("Throttle Step Size cannot exceed Max Throttle Speed");
        }
      }
    }
  } else {
    errors = [];

    // ToDo: improve errors below
    const { inputMode, inputLimitLower, inputLimitCenter, inputLimitUpper } =
      inputConfig;
    if (
      [inputMode, inputLimitLower, inputLimitCenter, inputLimitUpper].some(
        (v) => v === ""
      )
    ) {
      errors.push(
        "One or more of these are blank: input mode, lower, center, and/or upper input limit."
      );
    } else if (
      parseInt(inputLimitLower, 10) > parseInt(inputLimitCenter, 10) ||
      parseInt(inputLimitCenter, 10) > parseInt(inputLimitUpper, 10) ||
      parseInt(inputLimitLower, 10) < 0
    ) {
      errors.push(
        "Input limits must be as follows: 0 <= lower <= center <= upper."
      );
    }
  }

  if (!isPolePairCountValid(controlConfig.polePairCount)) {
    errors.push("Pole pair count must be a positive integer.");
  }

  return errors;
};

export const InputScreen = (
  props: INavigationProps & {
    inputConfig: Strings<InputConfig>;
    powerConfig: Strings<IWritePowerConfigResponse>;
    controlConfig: Strings<IWriteControlConfigRequest>;
    positionControlSettings: Strings<IWritePositionControlSettingsRequest>;
    posCtrlCalResultsStorable: boolean, // Indicates whether the fields for storing calibration results are in cstore
    setPosCtrlCalResults: Dispatch<SetStateAction<PosCtrlCalResults | undefined>>,
    editInputConfig: Edit<InputConfig>;
    editControlConfig: Edit<IWriteControlConfigRequest>;
    editPositionControlSettings: Edit<IWritePositionControlSettingsRequest>;
    setWarningContents: (contents: ReactNode) => void;
  }
) => {
  const {
    inputConfig,
    powerConfig,
    controlConfig,
    positionControlSettings,
    posCtrlCalResultsStorable,
    setPosCtrlCalResults,
    editInputConfig,
    editControlConfig,
    editPositionControlSettings,
    setWarningContents,
    prev,
    next,
  } = props;

  const lowerGreaterThanCenter =
    inputConfig.inputLimitLower !== "" &&
    inputConfig.inputLimitCenter !== "" &&
    parseInt(inputConfig.inputLimitLower, 10) >
      parseInt(inputConfig.inputLimitCenter, 10);
  const centerGreaterThanUpper =
    inputConfig.inputLimitCenter !== "" &&
    inputConfig.inputLimitUpper !== "" &&
    parseInt(inputConfig.inputLimitCenter, 10) >
      parseInt(inputConfig.inputLimitUpper, 10);

  const [selectedControlMode, setSelectedControlMode] = useState(
    controlConfig.controlMode
  );
  const [selectedPositionType, setSelectedPositionType] = useState(
    positionControlSettings.positionControlType
  );
  const [selectedPostitionUnits, setSelectedPositionUnits] = useState(
    positionControlSettings.positionUnits
  );
  const [selectedPositionResolution, setSelectedPositionResolution] = useState(
    positionControlSettings.positionResolution
  );
  const [showMinPositionModal, setShowMinPositionModal] =
    useState<boolean>(false);
  const [showMaxPositionModal, setShowMaxPositionModal] =
    useState<boolean>(false);
  const [showZeroSensorErrorModal, setShowZeroSensorErrorModal] =
    useState<boolean>(false);
  const rawPositionSensor = useSingleTelemetryValue( "rawPositionSensor" ) as number | undefined;
  const rawPositionSensorStr = rawPositionSensor?.toFixed(3) ?? "-----";

  // Homing state
  const { connectedDevice } = useDevices();
  const [isHoming, setIsHoming] = useState(false);
  const [homingStatus, setHomingStatus] = useState<HomingCalibStatus | null>(null);
  const [homingZeroOffset, setHomingZeroOffset] = useState<number | null>(null);
  const [positionSensorValue, setPositionSensorValue] = useState<number | null>(null);
  const [minPosition, setMinPosition] = useState<number | null>(null);
  const [maxPosition, setMaxPosition] = useState<number | null>(null);

  // Position sensor reading for homing
  const getPositionSensorReading = useCallback(async () => {
    const value = await packetThreadPoolApi.getPositionSensorReading();
    if (value !== null) {
      setPositionSensorValue(value);
      // Update min/max while homing
      if (isHoming) {
        setMinPosition((prev) => (prev === null ? value : Math.min(prev, value)));
        setMaxPosition((prev) => (prev === null ? value : Math.max(prev, value)));
      }
    }
    return value;
  }, [isHoming]);

  // Poll position sensor
  useEffect(() => {
    getPositionSensorReading();
    const interval = setInterval(getPositionSensorReading, POSITION_UPDATE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [getPositionSensorReading]);

  // Homing callback
  const handleStartHoming = useCallback(async () => {
    if (!connectedDevice?.handle) return;

    // Reset state
    setIsHoming(true);
    setHomingStatus(HomingCalibStatus.IN_PROGRESS);
    setHomingZeroOffset(null);
    setMinPosition(null);
    setMaxPosition(null);

    try {
      const result = await runHomingCalibration(connectedDevice.handle);
      setHomingStatus(result.status);
      setHomingZeroOffset(result.zeroOffset);
    } catch (error) {
      console.error("Homing failed:", error);
      setHomingStatus(HomingCalibStatus.FAILURE_TIMEOUT);
    } finally {
      setIsHoming(false);
    }
  }, [connectedDevice?.handle]);

  const isInputModeUSB = inputConfig.inputMode === InputMode.USB.toString();
  const isDisplayingPhysicalUnits = isInputModeUSB;
  const isControlModeSpeed =
    selectedControlMode === ControlMode.SPEED.toString();
  const isControlModePosition =
    selectedControlMode === ControlMode.POSITION.toString();
  // Check device's actual control mode (from cstore region) for homing visibility
  const isDeviceInPositionMode =
    connectedDevice?.initialized && connectedDevice.controlMode === ControlMode.POSITION;
  const handleControlModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const controlMode = e.target.value;
    setSelectedControlMode(controlMode);
    editControlConfig("controlMode", controlMode);
  };

  const handlePositionTypeChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const positionType = e.target.value;
    setSelectedPositionType(positionType);
    editPositionControlSettings("positionControlType", positionType);
  };

  const handlePositionUnitsChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const positionUnits = e.target.value;
    setSelectedPositionUnits(positionUnits);
    editPositionControlSettings("positionUnits", positionUnits);
  };

  const handlePositionResolutionChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const positionResolution = e.target.value;
    setSelectedPositionResolution(positionResolution);
    editPositionControlSettings("positionResolution", positionResolution);
    editInputConfig(
      "inputLimitUpper",
      calculateInputResolution(
        parseInt(positionResolution),
        parseInt(positionControlSettings.travelDistance)
      )
    );
  };

  const handleTravelDistanceChange = (value: number, valueStr: string) => {
    // setSelectedTravelDistance(valueStr);
    editPositionControlSettings("travelDistance", valueStr);
    editInputConfig(
      "inputLimitUpper",
      calculateInputResolution(parseInt(selectedPositionResolution), value)
    );
  };

  const calculateInputResolution = (
    resolution: number,
    travelDistance: number
  ): string => {
    switch (resolution) {
      case PositionResolution.VALUE_1E3:
        travelDistance *= 1000; // Thousandths
        break;
      case PositionResolution.VALUE_1E2:
        travelDistance *= 100; // Hundredths
        break;
      case PositionResolution.VALUE_1E1:
        travelDistance *= 10; // Tenths
        break;
      default:
        travelDistance *= 1; // Ones
        break;
    }
    return travelDistance.toString();
  };

  let stepFieldLabel: string | undefined;
  let stepField: keyof InputConfig | undefined;

  switch (selectedControlMode) {
    case ControlMode.SPEED.toString():
      stepFieldLabel = "Throttle Step Size (RPM)";
      stepField = "rpmStep";
      break;
    case ControlMode.TORQUE.toString():
      stepFieldLabel = "Throttle Step Size (Amps)";
      stepField = "ampsStep";
      break;
  }

  const throttleErrorsMemo = useMemo(
    () => throttleErrors(inputConfig, controlConfig),
    [inputConfig, controlConfig]
  );

  useEffect(() => {
    if (throttleErrorsMemo.length === 0) {
      setWarningContents(null);
      return;
    }

    const inputWarning = (
      <>
        <H4>Invalid Values</H4>
        <ul style={{ color: Colors.WHITE }}>
          {throttleErrorsMemo.map((error, i) => (
            <li key={i}>{error}</li>
          ))}
        </ul>
      </>
    );
    setWarningContents(inputWarning);
  }, [setWarningContents, throttleErrorsMemo]);

  const controlModeOptions = [
    { value: ControlMode.TORQUE, label: "Torque" },
    { value: ControlMode.SPEED, label: "Speed" },
    { value: ControlMode.POSITION, label: "Position" },
  ];

  const positionTypeOptions = [
    { value: PositionType.UNKNOWN, label: "Select" },
    { value: PositionType.LINEAR, label: "Linear" },
    { value: PositionType.ROTATIONAL, label: "Rotational" },
  ];

  const positionUnitsOptions =
    selectedPositionType === PositionType.LINEAR.toString()
      ? [
          { value: PositionUnits.UNKNOWN, label: "Select" },
          { value: PositionUnits.MILLIMETERS, label: "Millimeters" },
          { value: PositionUnits.INCHES, label: "Inches" },
        ]
      : selectedPositionType === PositionType.ROTATIONAL.toString()
        ? [
            { value: PositionUnits.UNKNOWN, label: "Select" },
            { value: PositionUnits.RADIANS, label: "Radians" },
            { value: PositionUnits.DEGREES, label: "Degrees" },
          ]
        : [
            {
              value: PositionUnits.UNKNOWN,
              label: "Select Position Type First",
            },
          ];

  const positionResolutionOptions = [
    { value: PositionResolution.UNKNOWN, label: "Select" },
    { value: PositionResolution.VALUE_1E0, label: "Ones" },
    { value: PositionResolution.VALUE_1E1, label: "Tenths" },
    { value: PositionResolution.VALUE_1E2, label: "Hundredths" },
    { value: PositionResolution.VALUE_1E3, label: "Thousandths" },
  ];

  const setMinPositionWithDareSensor = useCallback( async () => {
    setShowMinPositionModal(false);
    if ( rawPositionSensor === undefined || !connectedDevice?.handle ) return;

    // Zero sensor in RAM (no CStore flash, no reboot).
    // Firmware resets position tracking so display immediately shows ~0.
    const zeroResponse = await zeroDareSensor( connectedDevice.handle, true );

    // Wait for the DMA zero sequence to complete (~0.8ms at 10kHz),
    // then read back the cached offsets via a lightweight subcommand
    // that doesn't suffer from USB TX queue overflow.
    await new Promise( r => setTimeout( r, 200 ) );
    let readback;
    let attempts = 2;
    while ( !readback && attempts-- ) {
      readback = await readDareZeroResult( connectedDevice.handle );
    }

    console.log( "[MinSet] ZERO_DARE_SENSOR sent.",
      readback
        ? `Readback: enc0=${readback.encoder0ZeroPos}, enc1=${readback.encoder1ZeroPos}`
        : "No readback.",
      zeroResponse
        ? `Original: enc0=${zeroResponse.encoder0ZeroPos}, enc1=${zeroResponse.encoder1ZeroPos}`
        : "No original response."
    );

    if (!readback) {
      setShowZeroSensorErrorModal(true);
      return;
    }

    // minPosition is 0 by definition (sensor was just zeroed).
    // Zero offsets are deferred to Export via CStore.
    setPosCtrlCalResults( {
      minPosition: 0,
      maxPosition: NaN,
      dareZeroEncoder0: readback.encoder0ZeroPos,
      dareZeroEncoder1: readback.encoder1ZeroPos,
      dareCountDirection: undefined,
    } );
  }, [ connectedDevice, rawPositionSensor, setPosCtrlCalResults ] );

  return (
    <div style={configContainerStyle}>
      <div style={screenTitleStyle}>
        Throttle Settings
      </div>
      <div style={{ display: "flex", flexDirection: "row", gap: 32, alignItems: "flex-start" }}>
        {/* Left side - Throttle config */}
        <div style={{ width: FORM_WIDTH, maxWidth: "100%", marginBottom: 8 }}>
        {/* INPUT CONFIG OPTIONS */}
        {/* INPUT MODE */}
        <FormGroup
          label="Input Mode"
          labelInfo={<span style={{ color: Colors.RED }}>*</span>}
        >
          <HTMLSelect
            style={inputStyle}
            fill={true}
            value={inputConfig.inputMode}
            options={[
              { value: InputMode.NONE, label: "None" },
              { value: InputMode.CAN, label: "CAN" },
              { value: InputMode.SERVO, label: "Servo" },
              { value: InputMode.USB, label: "USB" },
            ]}
            onChange={(e) => editInputConfig("inputMode", e.target.value)}
          />
        </FormGroup>

        {/* CONTROL MODE CONFIGURATION OPTIONS */}

        {/* POLE PAIR COUNT */}
        <FormGroup
          label="Pole Pair Count"
          labelInfo={<span style={{ color: Colors.RED }}>*</span>}
        >
          <NumericInput
            fill={true}
            value={controlConfig.polePairCount}
            onValueChange={(_, valStr) =>
              editControlConfig("polePairCount", valStr)
            }
            stepSize={
              1e-15 /* accept any floating point for now; integer validation happens later */
            }
            minorStepSize={null}
            buttonPosition="none"
            placeholder="Pole Pair Count"
            style={inputStyle}
          />
        </FormGroup>

        {/* CONTROL MODE */}
        <FormGroup
          label="Control Mode"
          labelInfo={<span style={{ color: Colors.RED }}>*</span>}
        >
          <HTMLSelect
            style={inputStyle}
            fill={true}
            value={selectedControlMode}
            options={controlModeOptions}
            onChange={handleControlModeChange}
          />
        </FormGroup>

        {/* MAX THROTTLE SPEED */}
        {isControlModeSpeed && (
          <FormGroup
            label="Max Throttle Speed (RPM)"
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
          >
            <NumericInput
              fill={true}
              value={controlConfig.motorMaxSpeed}
              onValueChange={(_, valStr) =>
                editControlConfig("motorMaxSpeed", valStr)
              }
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              placeholder="Max Throttle Speed"
              style={inputStyle}
            />
          </FormGroup>
        )}

        {/* SPEED CONTROL PROPORTIONAL GAIN */}
        {(isControlModeSpeed || isControlModePosition) && (
          <FormGroup
            label="Speed Control Proportional Gain (N-m/s/(mech. rad))"
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
          >
            <NumericInput
              disabled={isControlModePosition}
              fill={true}
              value={controlConfig.speedControlProportionalGain}
              onValueChange={(_, valStr) =>
                editControlConfig("speedControlProportionalGain", valStr)
              }
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              placeholder="Speed Control Proportional Gain"
              style={inputStyle}
            />
          </FormGroup>
        )}

        {/* SPEED CONTROL INTEGRAL GAIN */}
        {(isControlModeSpeed || isControlModePosition) && (
          <FormGroup
            label="Speed Control Integral Gain (N-m/(mech. rad))"
            labelInfo={<span style={{ color: Colors.RED }}>*</span>}
          >
            <NumericInput
              disabled={isControlModePosition}
              fill={true}
              value={controlConfig.speedControlIntegralGain}
              onValueChange={(_, valStr) =>
                editControlConfig("speedControlIntegralGain", valStr)
              }
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              placeholder="Speed Control Integral Gain"
              style={inputStyle}
            />
          </FormGroup>
        )}

        {/* Position P Gain (float: 0-10.0) */}
        {isControlModePosition && (
          <FormGroup
            label={
              <>
                Position P Gain <span style={{ color: Colors.RED }}>*</span>
              </>
            }
            labelInfo={
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <Tooltip2 content={P_GAIN_TEXT}>
                  <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                </Tooltip2>
              </div>
            }
          >
            <NumericInput
              fill={true}
              value={positionControlSettings.positionControlProportionalGain}
              onValueChange={(_, valStr) =>
                editPositionControlSettings(
                  "positionControlProportionalGain",
                  valStr
                )
              }
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              placeholder="Position Control Proportional Gain"
              style={inputStyle}
            />
          </FormGroup>
        )}

        {/* Position I Gain (float: 0-10.0) */}
        {isControlModePosition && (
          <FormGroup
            label={
              <>
                Position I Gain <span style={{ color: Colors.RED }}>*</span>
              </>
            }
            labelInfo={
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <Tooltip2 content={I_GAIN_TEXT}>
                  <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                </Tooltip2>
              </div>
            }
          >
            <NumericInput
              fill={true}
              value={positionControlSettings.positionControlIntegralGain}
              onValueChange={(_, valStr) =>
                editPositionControlSettings(
                  "positionControlIntegralGain",
                  valStr
                )
              }
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              placeholder="Position Control Integral Gain"
              style={inputStyle}
            />
          </FormGroup>
        )}

        {/* Position D Gain (float: 0-10.0) */}
        {isControlModePosition && (
          <FormGroup
            label={
              <>
                Position D Gain <span style={{ color: Colors.RED }}>*</span>
              </>
            }
            labelInfo={
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <Tooltip2 content={D_GAIN_TEXT}>
                  <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                </Tooltip2>
              </div>
            }
          >
            <NumericInput
              fill={true}
              value={positionControlSettings.positionControlDerivGain}
              onValueChange={(_, valStr) =>
                editPositionControlSettings("positionControlDerivGain", valStr)
              }
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              placeholder="Position Control Derivative Gain"
              style={inputStyle}
            />
          </FormGroup>
        )}

        {/* Max Speed (rad/s) (float: 0-3000.0) */}
        {isControlModePosition && (
          <FormGroup
            label={
              <>
                Max Speed <span style={{ color: Colors.RED }}>*</span>
              </>
            }
            labelInfo={
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <Tooltip2 content={MOTOR_MAX_SPEED_TEXT}>
                  <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                </Tooltip2>
              </div>
            }
          >
            <NumericInput
              fill={true}
              value={positionControlSettings.positionControlMaxSpeed}
              onValueChange={(_, valStr) =>
                editPositionControlSettings("positionControlMaxSpeed", valStr)
              }
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              placeholder="Max Speed"
              style={inputStyle}
            />
          </FormGroup>
        )}

        {/* Max Acceleration (rad/s^2) (float: 0-30000.0) */}
        {isControlModePosition && (
          <FormGroup
            label={
              <>
                Max Acceleration <span style={{ color: Colors.RED }}>*</span>
              </>
            }
            labelInfo={
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <Tooltip2 content={MOTOR_MAX_ACCELERATION_TEXT}>
                  <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                </Tooltip2>
              </div>
            }
          >
            <NumericInput
              fill={true}
              value={positionControlSettings.positionControlMaxAccel}
              onValueChange={(_, valStr) =>
                editPositionControlSettings("positionControlMaxAccel", valStr)
              }
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              placeholder="Max Acceleration"
              style={inputStyle}
            />
          </FormGroup>
        )}

        {/* Deadband (rad) (float: 0-10.0) */}
        {isControlModePosition && (
          <FormGroup
            label={
              <>
                Deadband <span style={{ color: Colors.RED }}>*</span>
              </>
            }
            labelInfo={
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <Tooltip2 content={DEADBAND_TEXT}>
                  <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                </Tooltip2>
              </div>
            }
          >
            <NumericInput
              fill={true}
              value={positionControlSettings.positionControlDeadband}
              onValueChange={(_, valStr) =>
                editPositionControlSettings("positionControlDeadband", valStr)
              }
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              placeholder="Deadband"
              style={inputStyle}
            />
          </FormGroup>
        )}

        {/* POSITION TYPE */}
        {isControlModePosition && (
          <FormGroup
            label={
              <>
                Position Type <span style={{ color: Colors.RED }}>*</span>
              </>
            }
            labelInfo={
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <Tooltip2 content={POSITION_TYPE_TEXT}>
                  <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                </Tooltip2>
              </div>
            }
          >
            <HTMLSelect
              style={inputStyle}
              fill={true}
              value={selectedPositionType}
              options={positionTypeOptions}
              onChange={handlePositionTypeChange}
            />
          </FormGroup>
        )}

        {/* POSITION UNITS */}
        {isControlModePosition && (
          <FormGroup
            label={
              <>
                Position Units <span style={{ color: Colors.RED }}>*</span>
              </>
            }
            labelInfo={
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <Tooltip2 content={POSITION_UNITS_TEXT}>
                  <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                </Tooltip2>
              </div>
            }
          >
            <HTMLSelect
              style={inputStyle}
              fill={true}
              value={selectedPostitionUnits}
              options={positionUnitsOptions}
              onChange={handlePositionUnitsChange}
            />
          </FormGroup>
        )}

        {/* RESOLUTION */}
        {isControlModePosition && (
          <FormGroup
            label={
              <>
                Resolution <span style={{ color: Colors.RED }}>*</span>
              </>
            }
            labelInfo={
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <Tooltip2 content={RESOLUTION_TEXT}>
                  <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                </Tooltip2>
              </div>
            }
          >
            <HTMLSelect
              style={inputStyle}
              fill={true}
              value={selectedPositionResolution}
              options={positionResolutionOptions}
              onChange={handlePositionResolutionChange}
            />
          </FormGroup>
        )}

        {/* Travel Distance */}
        {isControlModePosition && (
          <FormGroup
            label={
              <>
                Travel Distance <span style={{ color: Colors.RED }}>*</span>
              </>
            }
            labelInfo={
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <Tooltip2 content={TRAVEL_DISTANCE_TEXT}>
                  <Icon icon="help" style={{ color: Colors.WHITE }}></Icon>
                </Tooltip2>
              </div>
            }
          >
            <NumericInput
              fill={true}
              value={positionControlSettings.travelDistance}
              onValueChange={handleTravelDistanceChange}
              stepSize={1e-15 /* accept any floating point */}
              minorStepSize={null}
              buttonPosition="none"
              placeholder="Travel Distance"
              style={inputStyle}
            />
          </FormGroup>
        )}

        {/* Calibrate Min position */}
        {isControlModePosition && (
          <div style={positionButtonContStyle}>
            <div style={{ flex: 1 }}>
              <Button
                backgroundColor={Colors.WHITE}
                textColor={Colors.BLACK}
                disabled={ !posCtrlCalResultsStorable }
                onClick={() => setShowMinPositionModal(true)}
              >
                Calibrate Min position
              </Button>
            </div>
          </div>
        )}

        {isControlModePosition && (
          <Dialog
            title="Calibrate Min Position"
            isOpen={showMinPositionModal}
            onClose={() => setShowMinPositionModal(false)}
            style={{ backgroundColor: Colors.LIGHT_GREEN }}
          >
            <div style={configModalStyle}>
              <div style={centerItemsStyle}>
                <h2>Move the motor to the minimum position and click Set.</h2>
              </div>
              <div style={centerItemsStyle}>
                <h2>{rawPositionSensorStr}</h2>
              </div>
              <div style={centerItemsStyle}>
                <Button
                  backgroundColor={Colors.WHITE}
                  textColor={Colors.BLACK}
                  onClick={setMinPositionWithDareSensor}
                >
                  Set
                </Button>
              </div>
            </div>
          </Dialog>
        )}

        {/* Calibrate Max position */}
        {isControlModePosition && (
          <div style={positionButtonContStyle}>
            <div style={{ flex: 1 }}>
              <Button
                backgroundColor={Colors.WHITE}
                textColor={Colors.BLACK}
                disabled={ !posCtrlCalResultsStorable }
                onClick={() => setShowMaxPositionModal(true)}
              >
                Calibrate Max position
              </Button>
            </div>
          </div>
        )}

        {isControlModePosition && (
          <Dialog
            title="Calibrate Max Position"
            isOpen={showMaxPositionModal}
            onClose={() => setShowMaxPositionModal(false)}
            style={{ backgroundColor: Colors.LIGHT_GREEN }}
          >
            <div style={configModalStyle}>
              <div style={centerItemsStyle}>
                <h2>Move the motor to the maximum position and click Set.</h2>
              </div>
              <div style={centerItemsStyle}>
                <h2>{rawPositionSensorStr}</h2>
              </div>
              <div style={centerItemsStyle}>
                <Button
                  backgroundColor={Colors.WHITE}
                  textColor={Colors.BLACK}
                  onClick={async () => {
                    setShowMaxPositionModal(false);
                    if ( rawPositionSensor === undefined || !connectedDevice?.handle ) return;

                    // Always update calibration state, regardless of firmware command success
                    setPosCtrlCalResults( ( orig ) => {
                      const minPos = orig?.minPosition ?? 0;
                      const maxPos = rawPositionSensor;
                      const direction = (maxPos - minPos) >= 0 ? 1 : -1;
                      return {
                        minPosition: orig?.minPosition ?? NaN,
                        maxPosition: maxPos,
                        dareZeroEncoder0: orig?.dareZeroEncoder0,
                        dareZeroEncoder1: orig?.dareZeroEncoder1,
                        dareCountDirection: direction,
                      };
                    });
                  }}
                >
                  Set
                </Button>
              </div>
            </div>
          </Dialog>
        )}

        {/* Zero Sensor Error Modal */}
        <Dialog
          title="Sensor Zero Error"
          isOpen={showZeroSensorErrorModal}
          onClose={() => setShowZeroSensorErrorModal(false)}
          style={{ backgroundColor: Colors.LIGHT_GREEN }}
        >
          <div style={configModalStyle}>
            <div style={centerItemsStyle}>
              <h2>Failed to read sensor zero offsets.</h2>
            </div>
            <div style={centerItemsStyle}>
              <p>Please try again or check the device connection.</p>
            </div>
            <div style={centerItemsStyle}>
              <Button
                backgroundColor={Colors.WHITE}
                textColor={Colors.BLACK}
                onClick={() => setShowZeroSensorErrorModal(false)}
              >
                OK
              </Button>
            </div>
          </div>
        </Dialog>

        {/* MTPA */}
        <FormGroup label="MTPA">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                fontWeight: "bold",
                textShadow: "0px 1px 3px rgba(0, 0, 0, 0.2)",
              }}
            >
              {parseInt(controlConfig.mtpa) === 1 ? "ENABLED" : "DISABLED"}
            </span>
            <ToggleButton
              value={parseInt(controlConfig.mtpa)}
              onChange={(value: number) => {
                editControlConfig("mtpa", String(value));
              }}
            />
          </div>
        </FormGroup>

        {/* Flux Weakening */}
        <FormGroup label="Flux Weakening">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                fontWeight: "bold",
                textShadow: "0px 1px 3px rgba(0, 0, 0, 0.2)",
              }}
            >
              {parseInt(controlConfig.fluxWeakening) === 1
                ? "ENABLED"
                : "DISABLED"}
            </span>
            <ToggleButton
              value={parseInt(controlConfig.fluxWeakening)}
              onChange={(value: number) => {
                editControlConfig("fluxWeakening", String(value));
              }}
            />
          </div>
        </FormGroup>

        {/* INPUT STEPS OR MAPPING */}
        {!isControlModePosition ? (
          isDisplayingPhysicalUnits ? (
            <FormGroup label={stepFieldLabel}>
              <NumericInput
                fill={true}
                value={inputConfig[stepField!]}
                onValueChange={(_, valStr) =>
                  editInputConfig(stepField!, valStr)
                }
                stepSize={1e-15 /* accept any floating point */}
                minorStepSize={null}
                buttonPosition="none"
                placeholder={stepFieldLabel}
                style={inputStyle}
                disabled={stepField === undefined}
              />
            </FormGroup>
          ) : (
            <FormGroup
              label="Input Mapping"
              labelInfo={<span style={{ color: Colors.RED }}>*</span>}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ flex: 1, marginRight: 8 }}>
                  <NumericInput
                    fill={true}
                    value={inputConfig.inputLimitLower}
                    onValueChange={(_, valStr) =>
                      editInputConfig(
                        "inputLimitLower",
                        valStr.replaceAll(/[.-]/g, "")
                      )
                    }
                    stepSize={
                      1e-15 /* accept any floating point for now; integer validation happens later */
                    }
                    minorStepSize={null}
                    buttonPosition="none"
                    placeholder="Lower"
                    style={inputStyle}
                  />
                </div>
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: 16,
                    color: lowerGreaterThanCenter ? Colors.RED : undefined,
                  }}
                >
                  {"<="}
                </div>
                <div style={{ flex: 1, marginLeft: 8, marginRight: 8 }}>
                  <NumericInput
                    fill={true}
                    value={inputConfig.inputLimitCenter}
                    onValueChange={(_, valStr) =>
                      editInputConfig(
                        "inputLimitCenter",
                        valStr.replaceAll(/[.-]/g, "")
                      )
                    }
                    stepSize={
                      1e-15 /* accept any floating point for now; integer validation happens later */
                    }
                    minorStepSize={null}
                    buttonPosition="none"
                    placeholder="Center"
                    style={inputStyle}
                  />
                </div>
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: 16,
                    color: centerGreaterThanUpper ? Colors.RED : undefined,
                  }}
                >
                  {"<="}
                </div>
                <div style={{ flex: 1, marginLeft: 8 }}>
                  <NumericInput
                    fill={true}
                    value={inputConfig.inputLimitUpper}
                    onValueChange={(_, valStr) =>
                      editInputConfig(
                        "inputLimitUpper",
                        valStr.replaceAll(/[.-]/g, "")
                      )
                    }
                    stepSize={
                      1e-15 /* accept any floating point for now; integer validation happens later */
                    }
                    minorStepSize={null}
                    buttonPosition="none"
                    placeholder="Upper"
                    style={inputStyle}
                  />
                </div>
              </div>
            </FormGroup>
          )
        ) : null}
        {selectedControlMode !== ControlMode.POSITION.toString() ? (
          <ThrottleRangeDisplay
            powerConfig={powerConfig}
            controlConfig={controlConfig}
            inputConfig={inputConfig}
          />
        ) : null}
        </div>

        {/* Vertical Divider - only show if device is in position mode */}
        {isDeviceInPositionMode && (
          <div style={{ width: 1, backgroundColor: Colors.LIGHT_GREEN, alignSelf: "stretch" }} />
        )}

        {/* Right side - Homing (only visible when device's cstore has Position control mode) */}
        {isDeviceInPositionMode && (
          <div style={{ width: FORM_WIDTH, maxWidth: "100%" }}>
            <H4 style={{ marginBottom: 16, color: Colors.WHITE }}>Homing</H4>

            {/* Position Sensor Reading */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 14, color: Colors.WHITE, opacity: 0.8, marginBottom: 4 }}>
                Position Sensor Reading
              </div>
              <div style={{ fontSize: 32, fontWeight: "bold", color: Colors.WHITE }}>
                {positionSensorValue !== null ? positionSensorValue.toFixed(4) : "---"} rad
              </div>
            </div>

            {/* Min/Max Position */}
            <div style={{ display: "flex", gap: 32, marginBottom: 24 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, color: Colors.WHITE, opacity: 0.8, marginBottom: 4 }}>
                  Min Position
                </div>
                <div style={{ fontSize: 24, fontWeight: "bold", color: "#ff6b6b" }}>
                  {minPosition !== null ? minPosition.toFixed(4) : "---"}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, color: Colors.WHITE, opacity: 0.8, marginBottom: 4 }}>
                  Max Position
                </div>
                <div style={{ fontSize: 24, fontWeight: "bold", color: "#69db7c" }}>
                  {maxPosition !== null ? maxPosition.toFixed(4) : "---"}
                </div>
              </div>
            </div>

            {/* Start Homing Button */}
            <div style={{ marginBottom: 16 }}>
              <Button
                backgroundColor={isHoming ? Colors.YELLOW : Colors.BUTTON_GREEN}
                textColor={isHoming ? Colors.BLACK : Colors.WHITE}
                onClick={handleStartHoming}
                disabled={isHoming || !connectedDevice?.handle}
              >
                {isHoming ? "Homing..." : "Start Homing"}
              </Button>
            </div>

            {/* Homing Status */}
            {homingStatus !== null && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, color: Colors.WHITE, opacity: 0.8, marginBottom: 4 }}>
                  Status
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: "bold",
                    color: homingStatus === HomingCalibStatus.SUCCESS ? "#69db7c" : homingStatus === HomingCalibStatus.IN_PROGRESS ? Colors.YELLOW : "#ff6b6b",
                  }}
                >
                  {homingStatusToString(homingStatus)}
                </div>
              </div>
            )}

            {/* Zero Offset */}
            {homingZeroOffset !== null && homingStatus === HomingCalibStatus.SUCCESS && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, color: Colors.WHITE, opacity: 0.8, marginBottom: 4 }}>
                  Zero Offset
                </div>
                <div style={{ fontSize: 20, fontWeight: "bold", color: Colors.WHITE }}>
                  {homingZeroOffset.toFixed(4)} rad
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: "flex", width: FORM_WIDTH, marginTop: 16 }}>
        <div style={{ flex: 1 }}>
          <Button
            backgroundColor={Colors.WHITE}
            textColor={Colors.BLACK}
            onClick={prev}
            disabled={prev === undefined}
          >
            Back
          </Button>
        </div>
        <div style={{ width: 8 }} />
        <div style={{ flex: 1 }} />
        <div style={{ width: 8 }} />
        <div style={{ flex: 1 }}>
          <Button
            backgroundColor={Colors.BUTTON_GREEN}
            textColor={Colors.WHITE}
            onClick={next}
            disabled={next === undefined}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
